import { useCallback, useMemo } from 'react';
import type { IAuthContext } from '../../contexts/authContext';
import { AUTH_UNAUTHENTICATED_EVENT } from '../../events';
import { ApoClient } from '../client';
import {
  signIn as signInMutation,
  logout as logoutMutation,
} from '../mutations/user';
import type { SignInResponse } from '../types/signin';
import { useLocalStorage } from './useLocalStorage';

const AUTH_TOKEN_LOCAL_STORAGE_KEY = 'auth-token';
const AUTH_USER_TYPE_LOCAL_STORAGE_KEY = 'auth-user-type';
const AUTH_USER_USER_NAME = 'auth-user-name';
const AUTH_USER_ID = 'auth-user-id';

/**
 * The concrete implementation of AuthContext as a custom Hook.
 */

export const useAuthProvider = (): IAuthContext => {
  const client = useMemo(() => new ApoClient(), []);
  const [token, setToken] = useLocalStorage(AUTH_TOKEN_LOCAL_STORAGE_KEY, '');
  const [userType, setUserType] = useLocalStorage(
    AUTH_USER_TYPE_LOCAL_STORAGE_KEY,
    '',
  );
  const [userName, setUserName] = useLocalStorage(AUTH_USER_USER_NAME, '');
  const [userId, setUserId] = useLocalStorage(AUTH_USER_ID, '');

  const getUserData = useCallback(async () => {
    try {
      if (token !== '') {
        client.addHeader('Authorization', token);
      }
    } catch (error: unknown) {
      window.dispatchEvent(new Event(AUTH_UNAUTHENTICATED_EVENT));
    }
  }, [client, token]);

  // implementation goes here
  const signIn = async (
    username: string,
    password: string,
  ): Promise<boolean> => {
    try {
      const response = await client.request<SignInResponse>(signInMutation, {
        username,
        password,
      });

      const signin = response.data.signin;

      setToken(signin.token);
      setUserType(signin.user_type.type);
      setUserName(signin.full_name);
      setUserId(signin.id);

      client.addHeader('Authorization', signin.token);

      return true;
    } catch (error) {
      console.error('Sign in Request went wrong', error);
      return false;
    }
  };

  const signOut = async () => {
    try {
      await client.request(logoutMutation, {});
    } catch (error) {
      console.error('Logout failed', error);
    }
    setToken('');
    // setUser(null)
    setUserType('');
    setUserId('');
    client.addHeader('Authorization', '');
  };

  return {
    token,
    userType,
    userName,
    userId,
    signIn,
    signOut,
    getUserData,
  };
};
